import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Offcanvas, Button } from "react-bootstrap";
import { ChatMessages } from "./сhat-messages";
import styles from "./chat.module.css";
import { HeaderChat } from "./chat-header";
import { useAuthStore } from "../../auth";
import {
  ArrowBarLeft,
  ArrowBarRight,
  ArrowsExpandVertical,
} from "react-bootstrap-icons";
import { Chat, useChatStore } from "@/entities/chat";
import { Message, useMessagesStore } from "@/entities/message";
import { useLocation, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { getChatName } from "../lib";
import { useFilterStore, useMessageSelectionStore, useModalWithVoiceGuard, useSaveDrafts, useScrollStore } from "../model";
import { ConfirmVoiceModal } from "@/shared/ui";
import { ChatList } from "./chat-list";
import { SearchResultsList } from "./search-results-list";
import { performSearch, useSearchStore } from "@/entities/search/model";

export const ChatComponent = () => {
  const {
    chats,
    count,
    currentChat,
    loading,
    selectChat,
    addNewChat,
    fetchChats,
    deleteChats,
    updateReadStatus,
  } = useChatStore();
  const allMessages = useMessagesStore((state) => state.messages);
  const allFiles = useMessagesStore((state) => state.files);
  const totalCount = useMessagesStore((state) => state.totalCount);
  const loadingFiles = useMessagesStore((state) => state.loadingFiles);

  const [isSharingMode, setIsSharingMode] = useState(false);

// Проверяем, запущено ли приложение через шеринг
useEffect(() => {
  const hasPendingText = localStorage.getItem('pendingSharedText');
  const hasPendingFiles = localStorage.getItem('pendingSharedFiles');
  
  if (hasPendingText || hasPendingFiles) {
    setIsSharingMode(true);
    setIsCollapsed(true)
  }
}, []);

// Сброс данных после отправки
useEffect(() => {
  return () => {
    if (!isSharingMode) {
      localStorage.removeItem('pendingSharedText');
      localStorage.removeItem('pendingSharedFiles');
    }
  };
}, [isSharingMode]);

  useEffect(() => {
    if (!currentChat) return;

    if (currentChat.unreadedMessageCount > 0 || currentChat.isReaded === false) {
      updateReadStatus([currentChat._id], true);
    }
  }, [currentChat]);

  useEffect(() => {
    if (typeof count === "number") {
      document.title = count > 0 ? `O!-Chat (${count})` : "O!-Chat";
    }
  }, [count]);

  const messages = useMemo(() => {
    if (!currentChat) return [];
    return allMessages[currentChat._id] ?? [];
  }, [allMessages, currentChat]);

  const files = useMemo(() => {
    if (!currentChat) return [];
    return allFiles[currentChat._id] ?? [];
  }, [allFiles, currentChat]);

  const loadMessages = useMessagesStore((state) => state.loadMessages);
  const loadFiles = useMessagesStore((state) => state.loadFiles);
  const getMessages = useMessagesStore((state) => state.getMessages);

  const [searchMode, setSearchMode] = useState(false);

  const { user, isAdmin } = useAuthStore((state) => state);
  useSaveDrafts();

  const containerRef = useRef<HTMLDivElement>(null);

  const [showSidebar, setShowSidebar] = useState(false);
  const [leftWidth, setLeftWidth] = useState(350);
  const [isResizing, setIsResizing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentDialog, setCurrentDialog] = useState<Message[]>([]);
  const currentChatName = getChatName(currentChat?.contact);

  const location = useLocation();
  const navigate = useNavigate();

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!chats.length || initializedRef.current) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const chat = searchParams.get("chat");

    if (chat) {
      const chatExists = chats.find((c) => c._id === chat);

      if (chatExists) {
        document.title = chatExists.contact.firstName || "Новый чат";
        selectChat(chatExists);
      } else {
        enqueueSnackbar(`Не удалось загрузить чат ${chat}`, {
          variant: "error",
        });
        navigate("/");
      }
    }
    initializedRef.current = true;
  }, [chats, location.search]);

  const updateQuery = (chatId: string) => {
    navigate(`/?chat=${chatId}`, { replace: true });
  };

  const triggerScrollToMessage = useCallback(
    (chatId: string, messageId: string) => {
      setSearchMode(false);
      useSearchStore.getState().setSearch("");

      const chat = useChatStore.getState().getChat(chatId);

      if (!chat) return;

      const scrollToFn = useScrollStore.getState().getScrollToMessage(chatId);
      scrollToFn?.(chatId, messageId);
    },
    []
  );

  // Модалка если произошел клик во время записи голосового
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"recording" | "voiceBlob" | null>(
    null
  );

  const selectedChatRef = useRef<Chat | null>(null);

  const { checkAndOpen, runDeferred } = useModalWithVoiceGuard();

  const [attachedFiles, setAttachedFiles] = useState<Record<string, File[]>>(
    {}
  );
    const setInputTitle = useMessagesStore((state) => state.setInputTitle);

const handleSelectChat = useCallback(
  (chat: Chat) => {
    if (!chat) return;

    // 🔍 Проверяем наличие данных шеринга
    const hasPendingText = localStorage.getItem('pendingSharedText');
    const hasPendingFiles = localStorage.getItem('pendingSharedFiles');

    const action = () => {
      // ✅ Существующая логика (НЕ МЕНЯЕМ)
      selectChat(chat);
      setShowSidebar(false);
      setSearchMode(false);
      updateQuery(chat._id);
      document.title = chat.contact.firstName || "Новый чат";
      
      useMessageSelectionStore.getState().resetSelection();
      useSearchStore.getState().setSearch("");

      // 🔥 НОВОЕ: Инжектим данные шеринга ПОСЛЕ выбора чата
      if (hasPendingText || hasPendingFiles) {
        // Текст
        if (hasPendingText) {
          setInputTitle(chat._id, hasPendingText);
          localStorage.removeItem('pendingSharedText');
        }

        // Файлы — вызываем глобальный обработчик из ChatMessages
        if (hasPendingFiles) {
          try {
            const base64Array = JSON.parse(hasPendingFiles);
            (window as any).handleSharedFiles?.(base64Array);
            localStorage.removeItem('pendingSharedFiles');
          } catch (e) {
            console.error('Error injecting shared files', e);
          }
        }
      }
    };

    // ✅ Существующая логика (НЕ МЕНЯЕМ)
    checkAndOpen(action, setModalType, setShowModal);
    selectedChatRef.current = chat;
  },
  [selectChat, setShowSidebar, setSearchMode, updateQuery, checkAndOpen]
);
  const MIN_LEFT = 66;
  const MIN_RIGHT = 350;

  useEffect(() => {
    if (user && !loading) {
      if(count !== 0 && count <= chats.length || chats.length >= 50) return;
      fetchChats(isAdmin);
    }
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.getBoundingClientRect().width;

      if (isCollapsed) return;

      if (containerWidth - leftWidth < MIN_RIGHT) {
        setLeftWidth(containerWidth - MIN_RIGHT);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [leftWidth, isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      let newLeftWidth =
        e.clientX - containerRef.current.getBoundingClientRect().left;
      if (newLeftWidth < MIN_LEFT) newLeftWidth = MIN_LEFT;

      const containerWidth = containerRef.current.getBoundingClientRect().width;
      const maxLeftWidth = containerWidth - (MIN_RIGHT + 15);

      if (newLeftWidth > maxLeftWidth) {
        newLeftWidth = maxLeftWidth;
      }

      setLeftWidth(newLeftWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.userSelect = "";
    };

    if (isResizing) {
      document.body.style.userSelect = "none";
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  useEffect(() => {
    if (!currentChat) return;

    const localMessages = getMessages(currentChat._id);

    if (totalCount[currentChat._id] !== undefined && localMessages.length) {
      setCurrentDialog(localMessages);
      return;
    }

    const fetchMessages = async () => {
      await loadMessages(currentChat._id);
      setCurrentDialog(getMessages(currentChat._id));
    };

    fetchMessages();
  }, [currentChat]);

  useEffect(() => {
    if (currentChat) {
      setCurrentDialog(messages);
    }
  }, [messages]);

  useEffect(() => {
    if (!currentChat || currentDialog.length === 0) return;

    const isLoading = loadingFiles[currentChat._id];

    if (isLoading === undefined) {
      loadFiles(currentChat._id);
    }
  }, [currentChat, currentDialog]);

  return (
    <>
      <Row
        className="g-2"
        ref={containerRef}
        style={{ height: "calc(100% - 40px)" }}
      >
          <Col
            sm={0}
            xs={4}
            className="d-none d-lg-block h-100"
            style={{
              width: isCollapsed ? 0 : leftWidth,
              minWidth: isCollapsed ? 0 : MIN_LEFT,
              maxWidth: leftWidth,
              borderRadius: 40,
              position: "relative",
              flex: "0 0 auto",
              paddingRight: 0,
              paddingLeft: 0,
              // transition: "width 0.1s ease, min-width 0.1s ease, max-width 0.1s ease",
            }}
          >
            <HeaderChat
              isCollapsed={isCollapsed}
              onFilterChange={(filters) => {
                useChatStore.getState().clearSelectedChats();
                
                const searchQuery = useSearchStore.getState().searchQuery;
                 if (!searchQuery) {
                  setSearchMode(false);
                    useChatStore.getState().fetchChats();
                } else {
                  setSearchMode(true);
                  performSearch(searchQuery);
                }
              }}
              handleSearch={(query) => {
                useChatStore.getState().clearSelectedChats();
                if (!query) {
                  setSearchMode(false);
                  if (!chats.length) {
                    useChatStore.getState().fetchChats();
                  }
                } else {
                  setSearchMode(true);
                  performSearch(query);
                }
              }}
            />

            {!searchMode && chats.length < 1 && (
              <div style={{ textAlign: "center", color: "gray" }}>
                Нет чатов
              </div>
            )}

            {
              searchMode ? (
                <SearchResultsList
                  userId={user?._id ?? ""}
                  onSelectChat={handleSelectChat}
                  getCurrentChatName={getChatName}
                  currentChatId={currentChat?._id}
                  onSelectMessage={(chatId, messageId) => triggerScrollToMessage(chatId, messageId)}
                />
              ) : (
                <ChatList
                  chats={chats.sort(
                    (a, b) =>
                      new Date(b.lastMessage?.createdAt ?? b.createdAt).getTime() -
                      new Date(a.lastMessage?.createdAt ?? a.createdAt).getTime()
                  )}
                  userId={user?._id ?? null}
                  currentChatId={currentChat?._id}
                  onSelectChat={handleSelectChat}
                  onSelectChats={(chatId) =>{}}
                  getCurrentChatName={getChatName}
                  onDelete={(chatId) => deleteChats([chatId])}
                />
              )
            }

            <Button
              variant="outline-secondary"
              className={`${styles.toggleButton} mb-2 rounded-circle d-flex align-items-center justify-content-center`}
              onClick={toggleCollapse}
              style={{
                position: "absolute",
                right: !isCollapsed ? (leftWidth < 70 ? 18 : 0) : -58,
                top: !isCollapsed ? 0 : 84,
                zIndex: 10,
                width: 38,
                height: 38,
                padding: 0,
              }}
            >
              {!isCollapsed ? <ArrowBarLeft /> : <ArrowBarRight />}
            </Button>
          </Col>

        {!isCollapsed && (
          <div
            onMouseDown={() => setIsResizing(true)}
            className={`d-none d-lg-block ${styles.resizingItem}`}
            style={{
              padding: 0,
              margin: "0 2px 0 6px",
              width: "4px",
              cursor: "col-resize",
              backgroundColor: "transparent",
              position: "relative",
            }}
          >
            <ArrowsExpandVertical
              color="grey"
              size={14}
              className="position-absolute top-50"
              style={{ zIndex: 999, left: -5 }}
            />
          </div>
        )}

        <Col
          sm={12}
          xs={6}
          className="h-100"
          style={{
            position: "relative",
            flex: 1,
            minWidth: MIN_RIGHT,
            background: "#fff",
          }}
        >
            <Button
              variant="light"
              onClick={() => setShowSidebar(true)}
              className={`d-lg-none ${styles.burgerBtn}`}
              style={{
                position: "absolute",
                top: 16,
                left: 24,
                height: 41,
                width: 41,
                zIndex: 1000,
                borderRadius: "50%",
              }}
            >
              ☰
            </Button>
{!isSharingMode && (
          <ChatMessages
          attachedFiles={attachedFiles}
          setAttachedFiles={setAttachedFiles}
            name={currentChatName}
            chat={currentChat}
            registeredContact={
              currentChat
                ? currentChat?.contact?.user?.status === "REGISTERED"
                : false
            }
            chatsLength={chats?.length || 0}
            messages={!!currentDialog?.length}
          />
            )}
        </Col>
      </Row>

      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="start"
        className="d-lg-none"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Чаты</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="pt-1 overflow-hidden">
            <HeaderChat
              isCollapsed={isCollapsed}
              onFilterChange={(filters) => {
                useChatStore.getState().clearSelectedChats();
                
                const searchQuery = useSearchStore.getState().searchQuery;
                 if (!searchQuery) {
                  setSearchMode(false);
                    useChatStore.getState().fetchChats();
                } else {
                  setSearchMode(true);
                  performSearch(searchQuery);
                }
              }}
              handleSearch={(query) => {
                useChatStore.getState().clearSelectedChats();
                if (!query) {
                  setSearchMode(false);
                  if (!chats.length) {
                    useChatStore.getState().fetchChats();
                  }
                } else {
                  setSearchMode(true);
                  performSearch(query);
                }
              }}
            />

          {
            searchMode ? (
              <SearchResultsList
                userId={user?._id ?? ""}
                onSelectChat={handleSelectChat}
                getCurrentChatName={getChatName}
                currentChatId={currentChat?._id}
                onSelectMessage={(chatId, messageId) => triggerScrollToMessage(chatId, messageId)}
              />
            ) : (
              <ChatList
                chats={chats.sort(
                  (a, b) =>
                    new Date(b.lastMessage?.createdAt ?? b.createdAt).getTime() -
                    new Date(a.lastMessage?.createdAt ?? a.createdAt).getTime()
                )}
                userId={user?._id ?? null}
                currentChatId={currentChat?._id}
                onSelectChat={handleSelectChat}
                onSelectChats={(chatId) =>{}}
                getCurrentChatName={getChatName}
                onDelete={(chatId) => deleteChats([chatId])}
              />
            )
          }

          {/* <ChatList
            chats={chats}
            userId={user?._id ?? null}
            currentChatId={currentChat?._id}
            onSelectChat={handleSelectChat}
            getCurrentChatName={getChatName}
            onDelete={deleteChat}
          /> */}
        </Offcanvas.Body>
      </Offcanvas>

      <ConfirmVoiceModal
        show={showModal}
        type={modalType}
        onClose={() => {
          setShowModal(false);
          setModalType(null);
        }}
        onConfirm={() => {
          runDeferred();
        }}
      />
    </>
  );
};
